import BaseView, { DomainModel3D } from './base-view';
import hitPlaneMaterial from '../view3d/hit-plane-material';
import logging from '../logging';
import pc from '../parametric-cache';
import { Mesh, BufferGeometry, Material, PlaneGeometry, Box3 } from 'three';
import { Parameter, ParametricPart } from '../domain';

const log = logging('view');


type ParametersObject = Record<string, string>;

export default class ParametricPartView extends BaseView {

    declare model: ParametricPart;

    mesh: Mesh;
    hit: Mesh;
    hitBox: Box3 = new Box3();

    constructor(model: DomainModel3D) {
        super(model);
        this.init();

        this.onReady.name = 'ParametricPartView';
    }

    make() {
        this.name = this.model.name;

        this.mesh = new Mesh(new BufferGeometry(), new Material());
        this.mesh.castShadow = this.mesh.receiveShadow = true;
        this.add(this.mesh);

        this.mesh.userData.isSelectable = true;
        this.mesh.userData.isMovable = true;
        this.mesh.userData.cursor = 'row-resize';
        this.mesh.userData.isParametricPart = true;

        this.highlights.add(this.mesh);

        this.hit = new Mesh(new PlaneGeometry(), hitPlaneMaterial);
        this.hit.name = 'ParametricPart hit rectangle';
        this.hit.visible = false;
        this.hit.userData.isSelectable = true;
        this.hit.userData.isMovable = true;
        this.add(this.hit);
    }

    bind() {
        const slots = pc.slots.get(this.model.name);
        if(slots) {
            this.bindMaterialArray(this.mesh, this.model.materials, slots);
        } else {
            this.bindMaterialArray(this.mesh, this.model.materials);
        }
        let changeSize = this.changeSize.bind(this);
        this.model.bind('width', changeSize);
        this.model.on('height', changeSize);
        this.model.on('depth', changeSize);
    
        this.model.bind('opacity', () => {
            const castShadow = this.model.opacity == 1;
            this.mesh.castShadow = castShadow;
        });}

    private async changeSize() {
        if(this.model.name == 'HangBar') {
            this.mesh.position.z = 12;
            this.updates.queue(
                async () => await pc.make(
                    this.model.name,
                    "Rod.obj", // TODO: update config w/ defaults
                    this.model.width),
                (geo) => {
                    this.mesh.geometry = geo;
                    this.updateHitSize();
                    this.onReady.trigger();
                }
            );
        } else {
            this.updates.queue(
                async () => await pc.make(this.model.name,
                    this.model.width, this.model.height, this.model.depth,
                    collapse(this.model.parameters)),
                (geo) => {
                    this.mesh.geometry = geo;
                    this.updateHitSize();
                    this.onReady.trigger();
                }
            );
        }
    }

    updateHitSize() {
        this.hitBox.setFromObject(this.mesh);
        const width = this.hitBox.max.x - this.hitBox.min.x;
        // Minimum height is 32mm, i.e. 32/25.4
        const height = Math.max(1.26, this.hitBox.max.y - this.hitBox.min.y);

        this.hit.scale.set(width, height, 1);
        this.hitBox.getCenter(this.hit.position);
    }

}

const collapse = (parameters: Parameter[]): ParametersObject => {
    const result: ParametersObject = {};
    for(const p of parameters)
    {
        if(p.key in result) {
            log.error(`ParametricPartView collapse: non-unique parameter key "${p.key}"; using first listed value "${result[p.key]}" and dropping second value "${p.value}"`);
        } else {
            result[p.key] = p.value;
        }
    }
    return result;
}
